import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Paper, Typography, TextField, Button, Stack } from '@mui/material';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { api } from '../api/client.js';
import { useRecaptcha } from '../components/Recaptcha.js';
import type { RecaptchaConfig } from '../lib/recaptcha.js';
import { useLang } from '../i18n/lang.js';
import { AUTH_COPY } from '../i18n/authCopy.js';
import { LangToggle } from '../i18n/LangToggle.js';

/**
 * Public "forgot password" request page. Posts the email and always shows the
 * same confirmation — the API never reveals whether an account exists.
 */
export function ForgotPasswordPage() {
  const [lang] = useLang();
  const c = AUTH_COPY[lang].forgot;
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  // This form is guarded by default: it is where a mailbox gets flooded with
  // reset links nobody asked for.
  const [recaptcha, setRecaptcha] = useState<RecaptchaConfig | null>(null);
  useEffect(() => {
    api
      .get('/v1/meta')
      .then((r) => setRecaptcha((r.data as { recaptcha?: RecaptchaConfig }).recaptcha ?? null))
      .catch(() => setRecaptcha(null));
  }, []);
  const captcha = useRecaptcha(recaptcha, 'forgotPassword');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/v1/auth/forgot-password', { email, recaptchaToken: await captcha.getToken() });
    } catch {
      // Swallow — we intentionally give no signal about the email's existence.
    } finally {
      setBusy(false);
      setSent(true);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2, bgcolor: 'background.default' }}>
      <Paper sx={{ p: 5, maxWidth: 440, width: '100%' }}>
        <LangToggle sx={{ float: 'right', mt: -2, mr: -2 }} />
        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 700, mb: 3, textAlign: 'center' }}>
          ReplayOps
        </Typography>

        {sent ? (
          <Stack spacing={2} alignItems="center" textAlign="center">
            <MarkEmailReadIcon sx={{ fontSize: 56, color: 'success.main' }} />
            <Typography variant="h6">{c.sentTitle}</Typography>
            <Typography color="text.secondary">{c.sentBody(email)}</Typography>
            <Button component={RouterLink} to="/login" variant="outlined">
              {c.back}
            </Button>
          </Stack>
        ) : (
          <form onSubmit={submit}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>{c.title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {c.body}
            </Typography>
            <TextField
              label={c.email}
              type="email"
              fullWidth
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {captcha.node}
            <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }} disabled={busy || !email}>
              {c.send}
            </Button>
            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
              <RouterLink to="/login">{c.back}</RouterLink>
            </Typography>
          </form>
        )}
      </Paper>
    </Box>
  );
}
